import {
  SESSIONS_TEST_IDS,
  type SessionLayer,
  TURN_LAYER_ORDER,
  type TurnLayer,
} from '@/components/admin/sessions/contracts';
import {
  isFarewellTurn,
  rereadLine,
  turnDefect,
  turnGuardrails,
  turnReading,
  turnRules,
} from '@/lib/conversation-model';
import { sessionTime } from '@/lib/session-labels';
import type { SessionTurn } from '@matter_extractor/shared';
import { Link as LinkIcon, Plus, TriangleAlert } from 'lucide-react';
import { LayerAppels } from './layer-appels';
import { LayerBrut } from './layer-brut';
import { LayerGardefous } from './layer-gardefous';
import { LayerMoteur } from './layer-moteur';
import { LayerObjectifs } from './layer-objectifs';
import type { ConversationSources } from './sources';

/**
 * ONE turn of the conversation (§12.3): the register, its marks, its « + » and
 * the five accordions it can reveal.
 *
 * ## A three-column register and not two bubbles
 *
 * The choice `transcript-reader.tsx` already made for the interviews, made again
 * here for the two reasons §12.3 gives: « un registre fait lire le rythme et
 * laisse la place à ce qui suit ». The second half is what bubbles could not
 * have: an accordion opening under an exchange without breaking the reading.
 *
 * ## At rest, nothing below the reply is in the DOM
 *
 * Not one accordion, not even closed - « un `details` fermé montre toujours sa
 * ligne de titre, donc cinq par tour feraient cinq lignes de bruit sur un
 * transcript de quarante tours ». A layer is mounted only when its bar switch is
 * on, or when the « + » of THIS turn was pressed; in the second case it is
 * mounted CLOSED, which is what the gesture promises.
 *
 * ## Three marks survive that rest, and only three
 *
 * The step band (drawn by the fil, above this component), a malformed turn, and
 * the incident of a « signaler seulement » guardrail. The last two « sont des
 * défauts du dispositif, pas des détails », and a defect asleep in an accordion
 * is a defect nobody reports.
 *
 * The malformed mark is NAMED since issue #96: « Tronqué » where the provider
 * cut the scribe at its ceiling, « Malformé » otherwise. Same mark, same count,
 * two repairs - and `turnDefect` of `conversation-model.ts` is the one place
 * that chooses. The mark builds its own sentence around the defect's `cause`,
 * as the three folds do around theirs, so nothing here reads `turn.truncated`
 * and no two of the four can drift.
 *
 * ## The farewell turn is a half-turn (issue #163)
 *
 * The goodbye of a farewell step is served and nobody answers it (§8.1): its
 * `reply` is null, and the person's row says « aucune réponse attendue » in
 * grey italics rather than standing empty. Its five folds stay one gesture
 * away like any turn's; the two that read the scribe - Objectifs, Garde-fous
 * et règles - say « non évalué », since no scribe was called (H81).
 *
 * ## The reread row is ONE line (issue #165)
 *
 * The reread on entry is a turn without an exchange (§8.1): its message is
 * empty and nobody answered, so the two lines of a turn would be two blanks.
 * It is drawn as one line of the register instead, right under the band of
 * the arriving step: « scribe » in the column of who speaks, and « Relecture à
 * l'entrée · relit <étape quittée> » followed by the objectives it succeeded,
 * or « rien de rempli » (§12.3). It stays a `turn` of the fil - « Tour n » copies
 * its link, the arrows walk onto it, its « + » reveals the layers of a turn - since
 * its snapshot is the state the next turn starts from. Garde-fous et règles
 * says « non évalué »: the scribe was called, but what it said of them is
 * ignored (H96). A malformed reread keeps its named mark, without « tour
 * compté »: it counts in no figure (H100).
 *
 * ## « Tour n » copies the link of the turn (issue #172)
 *
 * It used to pin the État column; the pin is gone - the column stays on the
 * last turn looked at - and the button now puts `…/protocole?tour=n` in the
 * clipboard, saying « Lien copié » in green for two seconds in its own place. A
 * small link icon announces it on the turn's hover. The turn the column shows
 * carries a bleu nuit bar on its left, and an arrival lights it for two
 * seconds (`data-flash`).
 */

/** What « Tour n » says after a press: the link is in the clipboard, or it could not be put there. */
export type LinkCopy = 'copied' | 'failed';

export interface TurnRowProps {
  turn: SessionTurn;
  /** The turn before it in the conversation - what a status CHANGE is read against. */
  previous: SessionTurn | undefined;
  /** 1-based, what « Tour n » prints and what `?tour=` carries. */
  number: number;
  sources: ConversationSources;
  /** The switches of the bar: a layer on is a layer open on every turn. */
  openLayers: ReadonlySet<SessionLayer>;
  /** The « + » of this turn was pressed: its five accordions exist, closed. */
  revealed: boolean;
  /**
   * The folds a HAND changed, keyed `<numéro>:<couche>` across the whole screen.
   *
   * An OVERRIDE of the switch and not a second « open » set: a fold closed by
   * hand under a switch that is on has to close, and a set that only ever said
   * « open » could not express it. Passed whole rather than sliced per turn - a
   * slice would be one `Map` built per turn on every hover.
   */
  folds: ReadonlyMap<string, boolean>;
  /** The État column shows this turn: the bleu nuit bar on its left. */
  marked: boolean;
  /**
   * A counter while an arrival lights this turn, null otherwise. A counter and
   * not a flag, so a second arrival on the same turn restarts the light.
   */
  flash: number | null;
  /** What « Tour n » says after a press, or null at rest. */
  copy: LinkCopy | null;
  onReveal: () => void;
  onFold: (layer: TurnLayer, open: boolean) => void;
  onCopyLink: () => void;
  /**
   * Focus, or a tap - the État column moves to this turn and STAYS there when
   * it leaves: there is no `onLeave` any more (issue #172).
   */
  onEnter: () => void;
  /** The pointer entered - the same, except while an arrival scrolls the page. */
  onHover: () => void;
}

export function TurnRow({
  turn,
  previous,
  number,
  sources,
  openLayers,
  revealed,
  folds,
  marked,
  flash,
  copy,
  onReveal,
  onFold,
  onCopyLink,
  onEnter,
  onHover,
}: TurnRowProps) {
  const reading = turnReading(sources.detail, turn, previous);
  // Null on a readable turn; « Malformé » or « Tronqué » otherwise (issue #96).
  const defect = turnDefect(turn);
  const guardrails = turnGuardrails(turn, sources.guardrails);
  const rules = turnRules(turn, sources.rules);
  const personaName = sources.detail.cascade.persona?.name ?? 'Interviewer';
  // The goodbye nobody answers (issue #163): no reply, and nothing evaluated.
  const farewell = isFarewellTurn(sources.detail.cascade.protocol, turn);
  // The reread on entry (issue #165): one line, and no exchange to draw.
  const reread = turn.reread;

  const layer = (which: TurnLayer) => {
    // Mounted when the switch is on, or when the « + » of THIS turn was pressed
    // - and in no other case. At rest the fil carries no accordion at all.
    if (!revealed && !openLayers.has(which)) return null;
    const open = folds.get(`${number}:${which}`) ?? openLayers.has(which);
    const onToggle = () => onFold(which, !open);
    switch (which) {
      case 'objectifs':
        return (
          <LayerObjectifs
            key={which}
            turn={turn}
            reading={reading}
            sources={sources}
            farewell={farewell}
            open={open}
            onToggle={onToggle}
          />
        );
      case 'gardefous':
        return (
          <LayerGardefous
            key={which}
            turn={turn}
            guardrails={guardrails}
            rules={rules}
            farewell={farewell}
            reread={reread}
            open={open}
            onToggle={onToggle}
          />
        );
      case 'appels':
        return (
          <LayerAppels
            key={which}
            turn={turn}
            budgetPerStepEur={sources.budgetPerStepEur}
            open={open}
            onToggle={onToggle}
          />
        );
      case 'brut':
        return (
          <LayerBrut
            key={which}
            sessionId={sources.detail.session.id}
            turn={turn}
            open={open}
            onToggle={onToggle}
          />
        );
      default:
        return (
          <LayerMoteur
            key={which}
            turn={turn}
            number={number}
            sources={sources}
            open={open}
            onToggle={onToggle}
          />
        );
    }
  };

  return (
    <li
      data-testid={SESSIONS_TEST_IDS.turn}
      data-turn={number}
      data-malformed={turn.malformed}
      data-truncated={turn.truncated}
      data-farewell={farewell}
      data-reread={reread}
      data-selected={marked}
      data-flash={flash !== null}
      /*
        Focusable so the arrows of the fil can walk the turns, and labelled so a
        screen reader announces which one it landed on. `tabIndex={0}` on every
        turn rather than a roving index: a transcript is a list of readable
        things and each of them is a legitimate stop, where a composite widget
        would have swallowed the two buttons a turn carries.
      */
      // biome-ignore lint/a11y/noNoninteractiveTabindex: §12.3 asks for keyboard navigation BETWEEN turns, and a turn is the unit that moves - the rule guards against tab stops that do nothing, where this one is the target of the arrows and of the État column.
      tabIndex={0}
      aria-label={`Tour ${number}`}
      /*
        No `onMouseLeave` and no `onBlur`: the column STAYS on the last turn
        looked at (issue #172), which is what lets the pointer travel from the
        fil to the column without the state switching under it. Under 768 px a
        tap is enough - it fires the mouse events of compatibility and focuses
        the turn, and either moves the column.
      */
      onMouseEnter={onHover}
      onFocus={onEnter}
      className={`group relative scroll-mt-40 border-gray-100 border-b border-l-3 last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
        marked ? 'border-l-bleu-nuit' : 'border-l-transparent'
      }`}
    >
      {reread ? (
        <RereadRow
          turn={turn}
          previous={previous}
          number={number}
          sources={sources}
          marked={marked}
          copy={copy}
          revealed={revealed}
          onCopyLink={onCopyLink}
          onReveal={onReveal}
        />
      ) : (
        <>
          <div className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 px-5 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] sm:gap-4">
            <time dateTime={turn.createdAt} className="pt-0.5 text-gray-500 text-xs tabular-nums">
              {sessionTime(turn.createdAt)}
            </time>
            <p className="font-semibold text-bleu-nuit-deep text-sm">
              {personaName}
              {turn.directive === null ? null : (
                <span className="block font-normal text-gray-500 text-xs">directive reçue</span>
              )}
            </p>
            <div className="col-span-2 min-w-0 sm:col-span-1">
              <p className="whitespace-pre-wrap break-words text-noir">{turn.message}</p>
            </div>
          </div>

          <div className="relative grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 bg-gray-50 px-5 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] sm:gap-4">
            <TurnLink
              number={number}
              marked={marked}
              copy={copy}
              onCopyLink={onCopyLink}
              className="self-start"
            />
            <p className="text-gray-700 text-sm">Personne</p>
            <div className="col-span-2 min-w-0 pr-9 sm:col-span-1">
              {farewell ? (
                /* The farewell turn (issue #163, §12.3): « la colonne de la
                   personne dit "aucune réponse attendue" en italique gris ». Said,
                   and not left blank - an empty cell would read as an empty
                   answer, which is exactly what the null reply exists to rule out
                   (H82). */
                <p data-testid={SESSIONS_TEST_IDS.turnNoReply} className="text-gray-500 italic">
                  aucune réponse attendue
                </p>
              ) : (
                <p className="whitespace-pre-wrap break-words text-gray-900">{turn.reply}</p>
              )}

              {turn.malformed || turn.incidents.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {defect === null ? null : (
                    <Mark>
                      {defect.label} · {defect.cause}, tour compté, aucun statut changé
                    </Mark>
                  )}
                  {turn.incidents.map((incident) => (
                    <Mark key={incident.guardrailId}>
                      Incident ·{' '}
                      {sources.guardrails.find((entry) => entry.id === incident.guardrailId)
                        ?.detection ?? incident.guardrailId}{' '}
                      (signaler seulement)
                    </Mark>
                  ))}
                </div>
              ) : null}
            </div>

            {/*
          The handle appears on hover or on focus, in the right margin of the
          reply - never at rest, which is the whole arrangement of §12.3. It
          stays in the DOM so the keyboard can reach it: an element that only
          existed under a pointer would be a gesture a keyboard cannot make.
        */}
            <MoreButton number={number} revealed={revealed} onReveal={onReveal} />
          </div>
        </>
      )}

      {TURN_LAYER_ORDER.map((which) => layer(which))}

      {flash === null ? null : (
        /*
          LAST, so it paints over the reply's line, which is positioned for its
          « + ». Keyed on the counter, so a second arrival on this turn replays
          the light.
        */
        <span
          key={flash}
          aria-hidden="true"
          className="turn-flash pointer-events-none absolute inset-0"
        />
      )}
    </li>
  );
}

/**
 * « Tour n », which copies the link of its turn (issue #172).
 *
 * The feedback stands IN the button's place for two seconds rather than in a
 * toast: the eye is on the button that was pressed. The name follows it, so a
 * screen reader hears the same thing the eye reads. A refused clipboard - an
 * insecure origin, a denied permission - says so rather than pretending: a
 * « Lien copié » over an empty clipboard is a link pasted into nothing.
 */
function TurnLink({
  number,
  marked,
  copy,
  onCopyLink,
  className = '',
}: {
  number: number;
  marked: boolean;
  copy: LinkCopy | null;
  onCopyLink: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      data-testid={SESSIONS_TEST_IDS.turnLink}
      data-copy={copy ?? ''}
      aria-label={
        copy === 'copied'
          ? `Lien du tour ${number} copié`
          : copy === 'failed'
            ? `Le lien du tour ${number} n’a pas pu être copié`
            : `Copier le lien du tour ${number}`
      }
      onClick={onCopyLink}
      className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-left font-semibold text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        copy === 'copied'
          ? 'text-vert-deep'
          : copy === 'failed'
            ? 'text-rouge-deep'
            : `hover:text-bleu-nuit ${marked ? 'text-bleu-nuit' : 'text-gray-500'}`
      } ${className}`}
    >
      {copy === 'copied' ? 'Lien copié' : copy === 'failed' ? 'Copie refusée' : `Tour ${number}`}
      {copy === null ? (
        <LinkIcon
          aria-hidden="true"
          className="size-3 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none"
        />
      ) : null}
    </button>
  );
}

/**
 * The « + » of one turn, in the right margin of its last line - and of one bubble
 * of a page played in the conversation (issue #186), which gives its own name
 * and its own mark.
 */
export function MoreButton({
  number,
  revealed,
  onReveal,
  label = `Détails du tour ${number}`,
  testId = SESSIONS_TEST_IDS.turnMore,
}: {
  number: number;
  revealed: boolean;
  onReveal: () => void;
  label?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-expanded={revealed}
      aria-label={label}
      onClick={onReveal}
      className={`absolute top-2.5 right-3 inline-flex size-6.5 cursor-pointer items-center justify-center rounded-full border bg-white opacity-0 transition-opacity focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none ${
        revealed
          ? 'border-bleu-nuit bg-bleu-nuit-tint text-bleu-nuit opacity-100'
          : 'border-gray-300 text-gray-600'
      }`}
    >
      <Plus
        aria-hidden="true"
        className={`size-3.5 transition-transform motion-reduce:transition-none ${
          revealed ? 'rotate-45' : ''
        }`}
      />
    </button>
  );
}

/**
 * The one line of the reread row (issue #165, §12.3). The time and « Tour n »
 * share the first column, since there is no second line to carry the link.
 */
function RereadRow({
  turn,
  previous,
  number,
  sources,
  marked,
  copy,
  revealed,
  onCopyLink,
  onReveal,
}: {
  turn: SessionTurn;
  previous: SessionTurn | undefined;
  number: number;
  sources: ConversationSources;
  marked: boolean;
  copy: LinkCopy | null;
  revealed: boolean;
  onCopyLink: () => void;
  onReveal: () => void;
}) {
  const line = rereadLine(sources.detail, turn, previous);
  const defect = turnDefect(turn);
  return (
    <div className="relative grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 bg-gray-50 px-5 py-3 sm:grid-cols-[3.5rem_6.5rem_minmax(0,1fr)] sm:gap-4">
      <div className="flex flex-col items-start gap-1">
        <time dateTime={turn.createdAt} className="pt-0.5 text-gray-500 text-xs tabular-nums">
          {sessionTime(turn.createdAt)}
        </time>
        <TurnLink number={number} marked={marked} copy={copy} onCopyLink={onCopyLink} />
      </div>
      <p className="font-semibold text-gray-700 text-sm">scribe</p>
      <div className="col-span-2 min-w-0 pr-9 sm:col-span-1">
        <p data-testid={SESSIONS_TEST_IDS.turnReread} className="break-words text-gray-800 text-sm">
          <span className="font-semibold">Relecture à l’entrée</span>
          {line.source === null ? null : <> · relit {line.source}</>}
          {' · '}
          {line.filled.length === 0 ? (
            <span className="text-gray-500 italic">rien de rempli</span>
          ) : (
            <>
              {line.filled.length > 1 ? 'réussis' : 'réussi'} d’entrée : {line.filled.join(', ')}
            </>
          )}
        </p>
        {defect === null ? null : (
          <div className="mt-2 flex flex-wrap gap-2">
            <Mark>
              {defect.label} · {defect.cause}, relecture sans effet
            </Mark>
          </div>
        )}
      </div>
      <MoreButton number={number} revealed={revealed} onReveal={onReveal} />
    </div>
  );
}

/** The yellow mark of a defect - the one thing allowed to shout in a bare fil. */
function Mark({ children }: { children: React.ReactNode }) {
  return (
    <span
      data-testid={SESSIONS_TEST_IDS.turnFlag}
      className="inline-flex items-center gap-1.5 rounded bg-jaune-tint px-2 py-0.5 font-semibold text-jaune-deep text-xs"
    >
      <TriangleAlert aria-hidden="true" className="size-3.5 shrink-0" />
      {children}
    </span>
  );
}
